import { effortMinutes } from './activity.js'
import { isWeekAchieved, type WeekSummary } from './goals.js'
import type { Activity } from './types.js'

/**
 * Les points débloquent les avatars et les tenues. Ils récompensent
 * exclusivement la régularité et l'effort fourni — jamais la performance.
 */
export const POINTS_PER_EFFORT_MINUTE = 1
export const POINTS_WEEKLY_GOAL_BONUS = 50
/** Bonus par semaine de série, plafonné pour ne pas exclure les nouveaux. */
export const POINTS_STREAK_BONUS_PER_WEEK = 10
export const POINTS_STREAK_BONUS_CAP = 100
export const POINTS_REFERRAL = 200

export function pointsForActivity(activity: Activity): number {
  return Math.round(effortMinutes(activity) * POINTS_PER_EFFORT_MINUTE)
}

export function pointsForWeek(
  summary: WeekSummary,
  weeklyGoalMin: number,
  streakWeeks: number,
): number {
  if (!isWeekAchieved(summary, weeklyGoalMin)) return 0
  const streakBonus = Math.min(
    POINTS_STREAK_BONUS_CAP,
    streakWeeks * POINTS_STREAK_BONUS_PER_WEEK,
  )
  return POINTS_WEEKLY_GOAL_BONUS + streakBonus
}

export function totalPoints(input: {
  activities: readonly Activity[]
  summaries: readonly WeekSummary[]
  weeklyGoalMin: number
  referrals?: number
}): number {
  let total = 0
  for (const activity of input.activities) total += pointsForActivity(activity)

  let streak = 0
  for (const summary of input.summaries) {
    if (isWeekAchieved(summary, input.weeklyGoalMin)) {
      total += pointsForWeek(summary, input.weeklyGoalMin, streak)
      streak += 1
    } else if (!summary.isCurrent) {
      streak = 0
    }
  }

  total += (input.referrals ?? 0) * POINTS_REFERRAL
  return total
}

export interface LevelInfo {
  level: number
  /** Points acquis dans le niveau courant */
  into: number
  /** Points nécessaires pour passer au niveau suivant */
  span: number
  /** 0..1 */
  progress: number
}

/**
 * Coefficient de la courbe de niveaux : seuil(n) = K x n x (n-1).
 *
 * Calibré pour qu'une pratique régulière (~300 pts/semaine) donne :
 * niveau 2 dès la première semaine (l'oeuf éclot vite), niveau 9 vers 10
 * semaines, niveau 14 vers 6 mois, niveau 20 (forme légendaire) vers un an.
 * Une progression trop lente tue l'envie de monter de niveau.
 */
export const LEVEL_CURVE_K = 40

/** Seuil cumulé pour atteindre le niveau `level` (niveau 1 = 0 point). */
export function pointsForLevel(level: number): number {
  return LEVEL_CURVE_K * level * (level - 1)
}

export function levelFromPoints(points: number): LevelInfo {
  const level = Math.max(
    1,
    Math.floor(
      (1 + Math.sqrt(1 + (4 * Math.max(0, points)) / LEVEL_CURVE_K)) / 2,
    ),
  )
  const floor = pointsForLevel(level)
  const ceiling = pointsForLevel(level + 1)
  const span = ceiling - floor
  const into = points - floor
  return { level, into, span, progress: span > 0 ? into / span : 0 }
}
